import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, downloadCsv, fmtUsd, fmtDate } from "@/lib/journal";
import { UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import { ErrorBoundary } from "@/components/ErrorBoundary";

const SOURCES = ["auto", "binance", "ibkr", "ibkr_txn", "etoro", "etoro_activity", "generic"];
const DATE_FORMATS = ["auto", "dmy", "mdy"];
const NUMBER_FORMATS = ["auto", "dot", "comma"];

export const CsvImportDialog = ({ open, onOpenChange, portfolios = [], defaultPortfolioId, onImported }) => {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [source, setSource] = useState("auto");
  const [dateFormat, setDateFormat] = useState("auto");
  const [numberFormat, setNumberFormat] = useState("auto");
  const [fileText, setFileText] = useState("");
  const [portfolioId, setPortfolioId] = useState(defaultPortfolioId);

  React.useEffect(() => {
    if (!portfolioId && portfolios.length) setPortfolioId(defaultPortfolioId || portfolios[0].id);
  }, [defaultPortfolioId, portfolios, portfolioId]);
  const [result, setResult] = useState(null);
  const [fileName, setFileName] = useState("");
  const [chosen, setChosen] = useState({});
  const [busy, setBusy] = useState(false);
  const [parseError, setParseError] = useState(null);
  const [report, setReport] = useState(null);

  const reset = () => { setResult(null); setChosen({}); setFileName(""); setParseError(null); setReport(null); setFileText(""); };

  const parseText = async (text, fmt = dateFormat, numFmt = numberFormat) => {
    setBusy(true);
    setParseError(null);
    try {
      const { data } = await api.post("/csv/parse", {
        content: text,
        source,
        fee_in_cost: true,
        date_format: fmt,
        number_format: numFmt,
      });
      if (data?.error || !Array.isArray(data?.rows)) {
        setResult(null);
        setParseError(data?.error || t("csv.unknownLayout"));
        return;
      }
      setResult(data);
      setChosen(Object.fromEntries(data.rows.filter((r) => r?.ok && r.row_id).map((r) => [r.row_id, true])));
      if (data.row_errors?.length) {
        setParseError(t("csv.skippedLines", { count: data.row_errors.length, details: data.row_errors.slice(0, 3).join(" | ") }));
      }
      toast.success(t("csv.detected", { format: String(data.format).toUpperCase(), count: data.parsed }));
    } catch (err) {
      console.error("CSV parse failed", err);
      setResult(null);
      setParseError(err?.response?.data?.detail || err?.message || t("csv.unreadableFile"));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const text = await file.text();
    setFileText(text);
    await parseText(text);
  };

  // Re-parse with the new day/month order so the preview never shows stale dates.
  const changeDateFormat = (next) => {
    setDateFormat(next);
    if (fileText) parseText(fileText, next, numberFormat);
  };

  // Re-parse with the new decimal convention so the preview shows the corrected values.
  const changeNumberFormat = (next) => {
    setNumberFormat(next);
    if (fileText) parseText(fileText, dateFormat, next);
  };

  const importRows = async () => {
    const rows = (result?.rows || []).filter((r) => chosen[r.row_id] && r.ok);
    if (!rows.length) return toast.error(t("csv.selectAtLeastOne"));
    setBusy(true);
    try {
      const { data } = await api.post("/csv/import", {
        portfolio_id: portfolioId === "all" ? null : portfolioId,
        source: result.format,
        rows,
      });
      const unreadable = (result.rows || [])
        .filter((r) => r && !r.ok)
        .map((r) => ({ asset: r.asset, side: r.side, volume: r.volume, price: r.price, date: r.date,
                       status: "skipped", reason: r.error || t("csv.report.unreadableRow") }));
      const deselected = (result.rows || [])
        .filter((r) => r && r.ok && !chosen[r.row_id])
        .map((r) => ({ asset: r.asset, side: r.side, volume: r.volume, price: r.price, date: r.date,
                       status: "skipped", reason: t("csv.report.notSelected") }));
      setReport({
        imported: data.imported,
        sells_applied: data.sells_applied,
        skipped: (data.skipped || 0) + unreadable.length + deselected.length,
        unmatched_sell_volume: data.unmatched_sell_volume,
        details: [...(Array.isArray(data.details) ? data.details : []), ...deselected, ...unreadable],
      });
      setResult(null);
      setChosen({});
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("csv.importFailed"));
    } finally {
      setBusy(false);
    }
  };

  const rows = Array.isArray(result?.rows) ? result.rows.filter(Boolean) : [];
  const previewRows = rows.slice(0, 100);
  const selectedCount = rows.filter((r) => chosen[r.row_id] && r.ok).length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto rounded-none border-[#1F1F1F] bg-[#0A0A0A]" data-testid="csv-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium text-white">
            {t("csv.title")} <span className="text-[#C58361]">{t("csv.titleAccent")}</span>
          </DialogTitle>
          <p className="text-sm text-zinc-500">
            {t("csv.description")}
          </p>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {report && (
            <ErrorBoundary onReset={reset}>
              <div className="space-y-3" data-testid="csv-import-report">
                <div className="grid grid-cols-3 gap-px border border-[#1F1F1F] bg-[#1F1F1F]">
                  <div className="bg-[#050505] px-3 py-3">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{t("csv.report.added")}</p>
                    <p data-testid="report-imported" className="font-mono text-lg text-[#4ADE80]">{report.imported}</p>
                  </div>
                  <div className="bg-[#050505] px-3 py-3">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{t("csv.report.sellsClosed")}</p>
                    <p data-testid="report-sells" className="font-mono text-lg text-[#C58361]">{report.sells_applied}</p>
                  </div>
                  <div className="bg-[#050505] px-3 py-3">
                    <p className="text-[9px] uppercase tracking-[0.2em] text-zinc-500">{t("csv.report.skipped")}</p>
                    <p data-testid="report-skipped" className="font-mono text-lg text-zinc-400">{report.skipped}</p>
                  </div>
                </div>

                {!!report.unmatched_sell_volume && (
                  <p className="font-mono text-[11px] text-[#EF4444]">
                    {t("csv.report.unmatched", { volume: report.unmatched_sell_volume })}
                  </p>
                )}

                <div className="max-h-[320px] divide-y divide-[#141414] overflow-y-auto border border-[#1F1F1F]">
                  {(report.details || []).slice(0, 200).map((d, i) => (
                    <div key={`d-${i}`} data-testid={`report-row-${i}`} className="flex items-start gap-3 px-3 py-2">
                      <span
                        className={`shrink-0 border px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${
                          d.status === "imported"
                            ? "border-[#4ADE80]/40 text-[#4ADE80]"
                            : d.status === "closed"
                            ? "border-[#C58361]/40 text-[#C58361]"
                            : "border-[#2A2A2A] text-zinc-500"
                        }`}
                      >
                        {t(`csv.status.${d.status}`, { defaultValue: d.status })}
                      </span>
                      <span className="min-w-0 flex-1 font-mono text-xs text-zinc-200">
                        {d.volume ?? "—"} {d.asset || "—"}
                        {d.price ? <span className="ml-2 text-zinc-500">@ {fmtUsd(d.price)}</span> : null}
                        <span className="ml-2 block text-[11px] text-zinc-500 sm:ml-2 sm:inline">
                          {d.code ? t(`csv.reason.${d.code}`, { defaultValue: d.reason }) : d.reason}
                        </span>
                      </span>
                      <span className="shrink-0 font-mono text-[10px] text-zinc-600">
                        {d.date ? fmtDate(d.date) : t("common.noDate")}
                      </span>
                    </div>
                  ))}
                </div>
                {(report.details || []).length > 200 && (
                  <p className="font-mono text-[10px] text-zinc-600">{t("csv.report.truncated", { total: report.details.length })}</p>
                )}

                <div className="flex gap-3">
                  <Button
                    data-testid="report-done-btn"
                    onClick={() => { reset(); onOpenChange(false); }}
                    className="h-10 flex-1 rounded-none bg-[#C58361] text-sm text-black hover:bg-[#D9956F]"
                  >
                    {t("common.done")}
                  </Button>
                  <Button
                    data-testid="report-import-more-btn"
                    onClick={() => setReport(null)}
                    className="h-10 rounded-none border border-[#1F1F1F] bg-transparent px-4 text-sm text-zinc-300 hover:bg-[#111111]"
                  >
                    {t("csv.report.importMore")}
                  </Button>
                </div>
              </div>
            </ErrorBoundary>
          )}

          {!report && (<>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("csv.source")}</Label>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger data-testid="csv-source-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {SOURCES.map((id) => (
                    <SelectItem key={id} value={id} data-testid={`csv-source-${id}`}>{t(`csv.sources.${id}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("csv.portfolio")}</Label>
              <Select value={portfolioId} onValueChange={setPortfolioId}>
                <SelectTrigger data-testid="csv-portfolio-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue placeholder={t("csv.choosePortfolio")} />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {portfolios.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`csv-portfolio-${p.id}`}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("csv.dateFormat")}</Label>
              <Select value={dateFormat} onValueChange={changeDateFormat}>
                <SelectTrigger data-testid="csv-date-format-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {DATE_FORMATS.map((id) => (
                    <SelectItem key={id} value={id} data-testid={`csv-date-format-${id}`}>
                      {t(id === "auto" ? "csv.dateAuto" : id === "dmy" ? "csv.dateDmy" : "csv.dateMdy")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-500">{t("csv.numberFormat")}</Label>
              <Select value={numberFormat} onValueChange={changeNumberFormat}>
                <SelectTrigger data-testid="csv-number-format-select" className="rounded-none border-[#1F1F1F] bg-[#050505]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {NUMBER_FORMATS.map((id) => (
                    <SelectItem key={id} value={id} data-testid={`csv-number-format-${id}`}>
                      {t(id === "auto" ? "csv.numberAuto" : id === "dot" ? "csv.numberDot" : "csv.numberComma")}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {result?.outliers > 0 && (
            <p data-testid="csv-outlier-banner" className="border border-[#EF4444]/40 bg-[#EF4444]/[0.06] px-3 py-2 font-mono text-[11px] text-[#EF4444]">
              {t("csv.outlierBanner", { count: result.outliers })}
            </p>
          )}
          {result?.date_ambiguous && dateFormat === "auto" && (
            <p data-testid="csv-date-ambiguous" className="border border-[#C58361]/40 bg-[#C58361]/[0.06] px-3 py-2 font-mono text-[11px] text-[#C58361]">
              {t("csv.dateAmbiguous", { sample: result.date_sample || "01/02/2026" })}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="csv-file-input" />
            <Button
              data-testid="csv-choose-file-btn"
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="h-9 rounded-none bg-[#C58361] px-4 text-xs text-black hover:bg-[#D9956F]"
            >
              <UploadSimple size={15} className="mr-2" /> {busy ? t("csv.reading") : t("csv.chooseFile")}
            </Button>
            <Button
              data-testid="csv-template-btn"
              onClick={() => downloadCsv("/csv/template", {}, "tj13-import-template.csv")}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-300 hover:bg-[#111111]"
            >
              <DownloadSimple size={15} className="mr-2" /> {t("csv.template")}
            </Button>
            {fileName && <span className="font-mono text-[11px] text-zinc-600">{fileName}</span>}
          </div>

          {parseError && (
            <div data-testid="csv-parse-error" className="border border-[#EF4444]/40 bg-[#EF4444]/5 px-3 py-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#EF4444]">{t("csv.problem")}</p>
              <p className="mt-1 break-words font-mono text-[11px] text-zinc-300">{String(parseError)}</p>
              <Button
                data-testid="csv-retry-btn"
                onClick={() => { setParseError(null); fileRef.current?.click(); }}
                className="mt-3 h-8 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-300 hover:bg-[#111111]"
              >
                {t("csv.tryAnother")}
              </Button>
            </div>
          )}

          {result && (
            <ErrorBoundary onReset={reset}>
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {t("csv.summary", { format: result.format, parsed: result.parsed, skipped: result.skipped })}
                </p>
                <span className="font-mono text-[11px] text-[#C58361]">{t("csv.selected", { count: selectedCount })}</span>
              </div>

              <div className="max-h-[320px] divide-y divide-[#141414] overflow-y-auto border border-[#1F1F1F]">
                {previewRows.map((r, idx) => (
                  <label
                    key={r.row_id || `row-${idx}`}
                    data-testid={`csv-row-${r.line}`}
                    className={`flex items-center gap-3 px-3 py-2 ${r.ok ? "cursor-pointer hover:bg-[#111111]" : "opacity-40"} ${
                      r.warnings?.length ? "border-l-2 border-[#EF4444]" : ""
                    }`}
                  >
                    <Checkbox
                      checked={!!chosen[r.row_id]}
                      disabled={!r.ok}
                      onCheckedChange={(v) => setChosen((p) => ({ ...p, [r.row_id]: !!v }))}
                      className="rounded-none border-[#2A2A2A] data-[state=checked]:border-[#C58361] data-[state=checked]:bg-[#C58361]"
                    />
                    <span
                      className={`border px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${
                        r.side === "sell" ? "border-[#EF4444]/40 text-[#EF4444]" : "border-[#4ADE80]/40 text-[#4ADE80]"
                      }`}
                    >
                      {t(`csv.side.${r.side}`, { defaultValue: r.side })}
                    </span>
                    <span className="min-w-0 flex-1 font-mono text-xs text-zinc-200">
                      {r.volume} {r.asset || "—"}
                      {r.price ? <span className="ml-2 text-zinc-500">@ {fmtUsd(r.price)}</span> : null}
                      {(r.raw_volume && String(r.raw_volume) !== String(r.volume)) ||
                      (r.raw_price && String(r.raw_price) !== String(r.price)) ? (
                        <span data-testid={`csv-parsed-hint-${r.line}`} className="ml-2 block text-[10px] text-zinc-600 sm:inline">
                          {[r.raw_volume, r.raw_price].filter(Boolean).join(" / ")} → {t("csv.parsedAs")} {r.volume} / {r.price}
                        </span>
                      ) : null}
                      {r.warnings?.length ? (
                        <span data-testid={`csv-row-warning-${r.line}`} className="ml-2 block text-[10px] text-[#EF4444] sm:inline">
                          {r.warnings.map((w) => t(`csv.warning.${w}`, { defaultValue: w })).join(", ")}
                        </span>
                      ) : null}
                      {r.error ? (
                        <span className="ml-2 text-[#EF4444]">
                          {(r.error_codes?.length
                            ? r.error_codes.map((c) => t(`csv.rowError.${c}`, { defaultValue: c }))
                            : [r.error]
                          ).join(", ")}
                        </span>
                      ) : null}
                    </span>
                    <span className="shrink-0 font-mono text-[10px] text-zinc-600">
                      {r.date ? fmtDate(r.date) : t("common.noDate")}
                    </span>
                  </label>
                ))}
              </div>
              {rows.length > previewRows.length && (
                <p data-testid="csv-preview-truncated" className="font-mono text-[10px] text-zinc-600">
                  {t("csv.truncated", { shown: previewRows.length, total: rows.length })}
                </p>
              )}
            </div>
            </ErrorBoundary>
          )}

          <Button
            data-testid="csv-import-btn"
            onClick={importRows}
            disabled={busy || !selectedCount}
            className="h-10 w-full rounded-none bg-[#C58361] text-sm text-black hover:bg-[#D9956F] disabled:opacity-40"
          >
            {busy ? t("csv.importing") : t("csv.importBtn", { count: selectedCount })}
          </Button>
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
